import * as React from "react";

import { Button } from "@/shared/foundation/primitives";

/** The part of a React Query `fetchNextPage()` result this needs: whether,
 *  AFTER that page landed, another one remains. */
export interface FetchNextPageResult {
  hasNextPage: boolean;
}

export interface LoadAllButtonProps {
  /** Whether a next page exists right now. The button is absent when it
   *  doesn't — there is nothing to offer. */
  hasNextPage: boolean;
  /** Fetches the next page and resolves with the state AFTER it landed. */
  fetchNextPage: () => Promise<FetchNextPageResult>;
}

/**
 * "Load all": drains every remaining page of a card's paginated query.
 *
 * A raw card draws its first page and stops, so the expensive part is opt-in
 * and named rather than hidden. Pressing this trades a wait for completeness,
 * and the card's caption says which of the two the user is looking at.
 *
 * Two things the loop deliberately does NOT do:
 *
 * It does not re-read a captured `hasNextPage`. That value is a render-time
 * snapshot and is stale the moment the first page lands, so the loop would
 * either stop after one page or never stop at all. It drives off what
 * `fetchNextPage()` itself resolves to instead.
 *
 * It does not run past its welcome. Every drain records the generation it
 * started in; unmounting the card — or pointing it at a different query —
 * bumps that generation, and the loop gives up at the next page boundary
 * rather than continuing to pull pages into a cache entry nobody is reading.
 */
export function LoadAllButton({
  hasNextPage,
  fetchNextPage,
}: LoadAllButtonProps) {
  const [draining, setDraining] = React.useState(false);

  // Bumped when the query being drained goes away (unmount) or is replaced.
  const generation = React.useRef(0);
  React.useEffect(
    () => () => {
      generation.current += 1;
    },
    [fetchNextPage],
  );

  const drain = async () => {
    const mine = generation.current;
    setDraining(true);
    try {
      let result = await fetchNextPage();
      while (result.hasNextPage && generation.current === mine) {
        result = await fetchNextPage();
      }
    } finally {
      if (generation.current === mine) {
        setDraining(false);
      }
    }
  };

  if (!hasNextPage) {
    return null;
  }

  return (
    <Button
      variant="text"
      size="small"
      loading={draining}
      disabled={draining}
      onClick={() => void drain()}
    >
      Load all
    </Button>
  );
}
